import React, { useEffect } from "react";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

export function SelectStringValue<T extends string>({
  id,
  values,
  onSelect,
  value,
  labelRenderer,
  disabled,
  className,
  disabledValue,
  placeholder,
  size
}: {
  id?: string;
  values?: T[];
  value?: T;
  onSelect?: (value?: T) => void;
  labelRenderer?: (value: T) => React.ReactNode;
  disabled?: boolean;
  className?: string;
  disabledValue?: (value: T) => boolean;
  placeholder?: string;
  size?: "default" | "sm" | "large";
}) {
  const [selected, setSelected] = React.useState<T | undefined>(value);

  useEffect(() => {
    setSelected(value);
  }, [value]);

  return (
    <Select
      value={selected}
      onValueChange={(value: T) => {
        setSelected(value);
        onSelect && onSelect(value);
      }}
    >
      <SelectTrigger
        size={size}
        className={cn("text-left", className)}
        disabled={disabled}
        id={id}
      >
        <SelectValue placeholder={placeholder ?? "Select value"} />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {values?.map(value => (
            <SelectItem
              key={value}
              value={value}
              disabled={disabledValue ? disabledValue(value) : false}
            >
              {labelRenderer ? labelRenderer(value) : value}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

export function SelectIdValue<T extends { id: K }, K extends string | number>({
  values: values,
  onSelect,
  value,
  toLabel,
  placeholder,
  disabled,
  deselectable,
  className
}: {
  values?: T[];
  value?: K;
  onSelect?: (value?: T) => void;
  toLabel?: (value: T) => React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  deselectable?: boolean;
}) {
  const [selected, setSelected] = React.useState<T | undefined>(
    values?.find(v => v.id === value)
  );

  useEffect(() => {
    setSelected(values?.find(v => v.id === value));
  }, [value, values]);

  return (
    <Select
      value={typeof selected?.id !== "undefined" ? String(selected?.id) : undefined}
      disabled={disabled}
      onValueChange={(id: string) => {
        if (id === "**empty**") {
          setSelected(undefined);
          if (onSelect) {
            onSelect(undefined);
          }
        } else {
          setSelected(values?.find(v => String(v.id) === id));
          if (onSelect) {
            onSelect(values?.find(v => String(v.id) === id));
          }
        }
      }}
    >
      <SelectTrigger className={cn("text-left", className)}>
        <SelectValue placeholder={placeholder ?? "Select value"} />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {deselectable && (
            <SelectItem value="**empty**">{placeholder ?? "Select value"}</SelectItem>
          )}
          {values?.map(value => (
            <SelectItem key={value.id} value={String(value.id)}>
              {toLabel ? toLabel(value) : value.id}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}
